import { useCallback, useMemo } from 'react';

import {
  useAvailableLegalClientStatusesLazy,
  useSaveLegalClientOnboardingStatus,
} from 'api/requests';
import { useSaveLegalClientAccountStatus } from 'api/requests/legal';
import { AvailableClientStatusesData } from 'api/types/data';
import {
  ClientAccountStatusType,
  LegalClientEntity,
  LegalClientOnboardingStatusType,
  OnboardingStatusType,
  PermissionType,
} from 'api/types/entity';
import { useServerErrorNotify } from 'hooks';
import { useTranslation } from 'libs/i18n';
import { useNotify } from 'libs/notify';
import { usePermissions } from 'libs/permissions';
import { useNotifySuccessSave } from 'modules/client/common/hooks';

import { filterVisibleOnboardingStatuses } from '../../common/helpers/filterVisibleOnboardingStatuses';

export const useLegalClientStatus = (legalClient: LegalClientEntity | null) => {
  const notifySuccessSave = useNotifySuccessSave();
  const notify = useNotify();
  const { t } = useTranslation();
  const { checkPermissions } = usePermissions();

  const legalClientId = legalClient?.id;
  const {
    availableStatuses,
    loading: fetchStatusesLoading,
    getAvailableLegalClientStatuses,
  } = useAvailableLegalClientStatusesLazy();

  const {
    loading: saveLegalClientOnboardingStatusLoading,
    saveLegalClientOnboardingStatus,
    error: saveLegalClientOnboardingStatusError,
  } = useSaveLegalClientOnboardingStatus();

  const {
    error: saveLegalClientAccountStatusError,
    loading: saveLegalClientAccountStatusLoading,
    saveLegalClientAccountStatus,
  } = useSaveLegalClientAccountStatus();

  const visibleOnboardingStatuses = useMemo(
    () => filterVisibleOnboardingStatuses(legalClient?.onboarding?.statuses),
    [legalClient?.onboarding?.statuses]
  );

  const statuses: AvailableClientStatusesData<LegalClientOnboardingStatusType> =
    useMemo(
      () => ({
        onboarding: availableStatuses?.onboarding ?? [],
        account: availableStatuses?.account ?? [],
      }),
      [availableStatuses?.account, availableStatuses?.onboarding]
    );

  const fetchAvailableStatuses = useCallback(() => {
    if (legalClientId) getAvailableLegalClientStatuses(legalClientId);
  }, [getAvailableLegalClientStatuses, legalClientId]);

  const onChangeOnboardingStatus = checkPermissions([
    PermissionType.Client_LegalClient_SaveOnboardingStatus,
  ])
    ? async (value: LegalClientOnboardingStatusType, result?: string) => {
        const updatedLegalClient = await saveLegalClientOnboardingStatus(
          legalClientId!,
          {
            value,
            result,
            versionNumber: legalClient?.versionNumber!,
          }
        );

        if (updatedLegalClient) {
          notifySuccessSave(
            value,
            'create',
            updatedLegalClient.isBrightSyncEnabled
          );

          if (
            value === OnboardingStatusType.AccountOpened &&
            updatedLegalClient?.personalAccount.number &&
            updatedLegalClient?.personalAccount.isCreatedBySystem
          ) {
            notify.info(
              t('client.client.statuses.personalAccountOpenedNotifyText')
            );
          }
        }
      }
    : undefined;

  const onChangeAccountStatus = checkPermissions([
    PermissionType.Client_LegalClient_SaveAccountStatus,
  ])
    ? async (value: ClientAccountStatusType) => {
        const updatedClient = await saveLegalClientAccountStatus(
          legalClientId!,
          {
            value,
            versionNumber: legalClient?.versionNumber!,
          }
        );

        if (updatedClient) {
          notifySuccessSave();
        }
      }
    : undefined;

  useServerErrorNotify(saveLegalClientOnboardingStatusError);
  useServerErrorNotify(saveLegalClientAccountStatusError);

  return {
    statuses,
    loading: fetchStatusesLoading,
    submitLoading:
      saveLegalClientOnboardingStatusLoading ||
      saveLegalClientAccountStatusLoading,
    fetchAvailableStatuses,
    onChangeOnboardingStatus,
    visibleOnboardingStatuses,
    onChangeAccountStatus,
  };
};
